import { BookingRequest, VendorBookingNote, Note, BookingRequestStatus, ArrayResponse, BaseSearchParams } from 'common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SortDirection } from '@angular/material/sort';
import { Injectable, inject } from '@angular/core';
import { delay, map, Observable, of } from 'rxjs';
import { AuthService } from '@ng-shared';

export interface BookingRequestFilterCriteria extends BaseSearchParams {
    vendorId?: number;
    statuses?: BookingRequestStatus[];
}

/**
 * Raw API response from /api/vendorBooking/search before data parsing
 */
interface VendorBookingApi extends Omit<BookingRequest, 'data'> {
    id: number;
    vendorId: number;
    vendorBookingStatus: number;
    statusDate: string;
    data: string;  // JSON string to be parsed
    vendor?: {
        id: number;
        name: string;
    };
}

/**
 * Payload for creating a new vendor booking note.
 */
export interface CreateVendorBookingNotePayload {
    vendorBookingId: number;
    note: Omit<Note, 'id'>;
}

/**
 * Payload for updating an existing vendor booking note.
 */
export interface UpdateVendorBookingNotePayload {
    id: number;
    vendorBookingId: number;
    noteId: number;
    note: Note;
}

@Injectable({
    providedIn: 'root'
})
export class BookingRequestsService {

    private readonly authService: AuthService = inject(AuthService);

    private readonly useMocks = USE_MOCKS;

    private get baseUrl(): string {
        return API_BASE_URL + '/api';
    }

    private get authHeaders(): { headers: HttpHeaders } {
        return {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': this.authService.bearerToken
            })
        };
    }

    constructor(private readonly httpClient: HttpClient) {
    }

    /**
     * Fetches booking requests from the API.
     * @param filters Filter criteria including vendorId and statuses
     * @param page Page number (1-indexed)
     * @param pageSize Number of items per page
     */
    public fetchBookingRequests(filters: BookingRequestFilterCriteria, page: number, pageSize: number, sort: string = 'statusDate',
        sortDirection: SortDirection = 'asc'): Observable<ArrayResponse<BookingRequest>> {

        if (this.useMocks) {
            const statuses = filters.statuses || [];

            return this.mockRequest<BookingRequest[]>(this.getMockBookingRequests(statuses))
                .pipe(
                    map((data: BookingRequest[]) => ({
                        result: data,
                        total: data.length
                    } as ArrayResponse<BookingRequest>))
                );
        }

        // TODO this is nasty....
        const payload: any = {
            ...filters,
            pageNumber: page,
            page: page,
            filter: filters.filter || '',
            pageSize: pageSize,
            sort: sort,
            sortBy: sort,
            sortDirection: sortDirection,
            paginationOptions: {
                pageNumber: page,
                pageSize: pageSize,
                sortBy: sort,
                sort: sortDirection,
            }
        };

        return this.httpClient.post<ArrayResponse<VendorBookingApi>>(
            `${this.baseUrl}/vendorBooking/search`,
            payload,
            this.authHeaders
        ).pipe(
            map((response: ArrayResponse<VendorBookingApi>) => {
                // Parse result if it's a string (backend may return JSON string)
                let results: VendorBookingApi[] = response.result as VendorBookingApi[];

                if (typeof response.result === 'string') {
                    results = JSON.parse(response.result);
                }

                // Parse data JSON string on each item
                const bookingRequests: BookingRequest[] = results.map((item) => ({
                    ...item,
                    data: item.data && typeof item.data === 'string' ? JSON.parse(item.data) : item.data
                }));

                return {
                    result: bookingRequests,
                    total: response.total
                } as ArrayResponse<BookingRequest>;
            })
        );
    }

    public updateBookingRequestStatus(booking: BookingRequest, status: BookingRequestStatus, vendorId?: number): Observable<BookingRequest> {
        const payload: VendorBookingApi = {
            ...booking,
            vendorBookingStatus: status,
            statusDate: new Date().toISOString(),
            data: JSON.stringify(booking.data)
        };

        if (vendorId) {
            payload.vendorId = vendorId;
        }

        if (this.useMocks) {
            // Return a minimal mock response for status update
            const mockResponse: Partial<BookingRequest> = {
                ...payload,
                id: booking.id,
                data: { name_surname: '', email: '', phone: '' }
            };
            return this.mockRequest<BookingRequest>(mockResponse as BookingRequest);
        }

        return this.httpClient.post<BookingRequest>(
            `${this.baseUrl}/vendorBooking/save`,
            payload,
            this.authHeaders
        );
    }

    /**
     * Silently marks a booking as viewed (from Assigned → Viewed).
     * Called when a vendor expands/views a booking request row.
     */
    public markAsViewed(bookingRequest: BookingRequest, vendorId?: number): Observable<BookingRequest> {
        return this.updateBookingRequestStatus(bookingRequest, BookingRequestStatus.Viewed, vendorId);
    }

    /**
     * @deprecated - not required if the SP_search is used.
     */
    public fetchNotes(vendorBookingId: number): Observable<VendorBookingNote[]> {
        if (this.useMocks) {
            return this.mockRequest<VendorBookingNote[]>(this.getMockNotes(vendorBookingId));
        }
        else {
            return this.httpClient.get<VendorBookingNote[]>(
                `${this.baseUrl}/vendorBookingNote/${vendorBookingId}`,
                this.authHeaders
            );
        }
    }

    public createNote(vendorBookingId: number, subject: string, userEmail: string, body?: string): Observable<VendorBookingNote> {
        const payload: CreateVendorBookingNotePayload = {
            vendorBookingId: vendorBookingId,
            note: {
                user: userEmail,
                date: new Date(),
                subject,
                body
            }
        };

        if (this.useMocks) {
            const mockNote: VendorBookingNote = {
                id: Date.now(),
                vendorBookingId: vendorBookingId,
                note: {
                    id: Date.now() + 1,
                    ...payload.note,
                    date: new Date()
                }
            };
            return this.mockRequest<VendorBookingNote>(mockNote);
        } else {
            return this.httpClient.post<VendorBookingNote>(
                `${this.baseUrl}/vendorBookingNote`,
                payload,
                this.authHeaders
            );
        }
    }

    /**
     * Only the original author can update their note.
     * @param note The full VendorBookingNote with updated content
     */
    public updateNote(note: VendorBookingNote): Observable<VendorBookingNote> {
        const payload: UpdateVendorBookingNotePayload = {
            id: note.id,
            vendorBookingId: note.vendorBookingId,
            noteId: note.note.id!,
            note: {
                ...note.note,
                date: new Date()
            }
        };

        if (this.useMocks) {
            return this.mockRequest<VendorBookingNote>({
                ...note,
                note: { ...note.note, date: new Date() }
            });
        }
        else {
            return this.httpClient.post<VendorBookingNote>(
                `${this.baseUrl}/vendorBookingNote`,
                payload,
                this.authHeaders
            );
        }
    }

    public deleteNote(vendorBookingNoteId: number): Observable<void> {
        if (this.useMocks) {
            return this.mockRequest<void>(undefined);
        }
        else {
            return this.httpClient.get<void>(
                `${this.baseUrl}/vendorBookingNote/remove/${vendorBookingNoteId}`,
                this.authHeaders
            );
        }
    }

    public mockRequest<T>(data?: T, extendDelayMs?: number): Observable<T> {
        const randomDelay: number = Math.floor(Math.random() * 2000) + 500 + (extendDelayMs || 0);

        return (of(data) as Observable<T>)
            .pipe(delay(randomDelay));
    }

    protected mockObs<T>(data?: T, throwError = false): Observable<T> {
        return new Observable((observer) => {

            // Random delay to simulate API latency, between 500ms and 2500ms.
            const mockApiRuntime = Math.floor(Math.random() * 2000) + 500;

            setTimeout(() => {

                // Throw an error if requested.
                if (throwError) {
                    observer.error('An error occurred.');
                    return;
                }

                // Emit the data if it is provided.
                if (data) observer.next(data);
                // Otherwise emit nothing.
                else observer.next();

                // Complete the observable.
                observer.complete();

            }, mockApiRuntime);

        });
    }

    // TODO move to JSON files:
    // --
    private readonly mockNotesData: VendorBookingNote[] = [
        {
            id: 1,
            vendorBookingId: 1,
            noteId: 101,
            note: {
                id: 101,
                user: 'dr.smith@visioncare.com',
                date: new Date('2024-01-15T11:30:00'),
                subject: 'Availability Confirmation',
                body: 'Customer called to confirm availability. Prefers afternoon appointments.'
            }
        },
        {
            id: 2,
            vendorBookingId: 1,
            noteId: 102,
            note: {
                id: 102,
                user: 'dr.smith@visioncare.com',
                date: new Date('2024-01-15T14:20:00'),
                subject: 'Insurance Follow-up',
                body: 'Follow-up needed regarding insurance coverage.'
            }
        },
        {
            id: 3,
            vendorBookingId: 2,
            noteId: 103,
            note: {
                id: 103,
                user: 'nurse.johnson@visioncare.com',
                date: new Date('2024-01-16T15:00:00'),
                subject: 'Medical History Review',
                body: 'Customer mentioned previous eye surgery. Need to review history.'
            }
        },
        {
            id: 4,
            vendorBookingId: 3,
            noteId: 104,
            note: {
                id: 104,
                user: 'dr.smith@visioncare.com',
                date: new Date('2024-01-17T10:15:00'),
                subject: 'Appointment Scheduled',
                body: 'Appointment scheduled for Tuesday at 2pm. Contact lenses consultation booked.'
            }
        }
    ];

    private getMockNotes(vendorBookingId: number): VendorBookingNote[] {
        return this.mockNotesData.filter(note => note.vendorBookingId === vendorBookingId);
    }

    private getMockBookingRequests(statuses: BookingRequestStatus[]): BookingRequest[] {
        const baseRequests: BookingRequest[] = [
            // Open requests (Assigned)
            {
                id: 1,
                vendorId: 1,
                vendorBookingStatus: BookingRequestStatus.Assigned,
                statusDate: '2024-01-15T10:30:00',
                data: {
                    name_surname: 'John Smith',
                    email: 'john.smith@email.com',
                    phone: '+27123456789',
                    area: 'Cape Town',
                    availability: 'Weekdays after 2pm',
                    message: 'Looking for a routine eye exam'
                },
                vendor: { id: 1, name: 'Vision Care Centre' },
                notes: this.getMockNotes(1)
            },
            // Open requests (Viewed)
            {
                id: 2,
                vendorId: 1,
                vendorBookingStatus: BookingRequestStatus.Viewed,
                statusDate: '2024-01-16T15:00:00',
                data: {
                    name_surname: 'Sarah Johnson',
                    email: 'sarah.johnson@email.com',
                    phone: '+27987654321',
                    area: 'Johannesburg',
                    availability: 'Any weekday morning',
                    message: 'Need new glasses, prescription has changed'
                },
                vendor: { id: 1, name: 'Vision Care Centre' },
                notes: this.getMockNotes(2)
            },
            // Booked request
            {
                id: 3,
                vendorId: 1,
                vendorBookingStatus: BookingRequestStatus.Booked,
                statusDate: '2024-01-17T11:00:00',
                data: {
                    name_surname: 'David Williams',
                    email: 'david.williams@email.com',
                    phone: '+27555123456',
                    message: 'General inquiry about contact lens options'
                },
                vendor: { id: 1, name: 'Vision Care Centre' },
                notes: this.getMockNotes(3)
            },
            // Completed request (Closed)
            {
                id: 4,
                vendorId: 1,
                vendorBookingStatus: BookingRequestStatus.Completed,
                statusDate: '2024-01-12T14:00:00',
                data: {
                    name_surname: 'Emily Brown',
                    email: 'emily.brown@email.com',
                    phone: '+27111222333',
                    area: 'Durban',
                    message: 'Completed eye examination'
                },
                vendor: { id: 1, name: 'Vision Care Centre' },
                notes: []
            },
            // Cancelled request (Closed)
            {
                id: 5,
                vendorId: 1,
                vendorBookingStatus: BookingRequestStatus.Cancelled,
                statusDate: '2024-01-09T10:00:00',
                data: {
                    name_surname: 'Michael Davis',
                    email: 'michael.davis@email.com',
                    phone: '+27444555666',
                    message: 'Customer cancelled appointment'
                },
                vendor: { id: 1, name: 'Vision Care Centre' },
                notes: []
            }
        ];

        // Filter by statuses if provided, otherwise return all
        if (statuses.length === 0) {
            return baseRequests;
        }
        return baseRequests.filter(req => statuses.includes(req.vendorBookingStatus));
    }
    // --

}